import { useCallback, useEffect, useRef } from 'react';
import assign from 'lodash/assign';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';
import moment from '@g4/moment-utc';
import _ from 'lodash';
import { useSSRQuery } from '../graphql';
import { useMutation } from '@apollo/react-hooks';
import useBookingSteps from './useBookingSteps';
import UPLIFT_CONFIG_QUERY from './useUplift/upliftConfig.query.graphql';
import SELECT_UPLIFT from './useUplift/selectUplift.mutation.graphql';
import * as UpLiftAPI from './useUplift/upLiftApi';
import Wait from './useUplift/wait';
import { useUpliftState } from '../components/UpliftStateProvider';
import GET_ORDER_PAYMENTS_QUERY from '../pageComponents/paymentPage/promoVouchersPointsForms/getOrderPayments.query.graphql';

/* Contents
- constants
- helper functions
- hook code
  - state & init
  - initPayMonthly function
  - onChange handler
  - buildTrioInfo function
  - buildPricesInfo function
  - upReady function: onUpliftReady
  - select payment function
  - deselect payment function

  - setings query & initialization of uplift script. call to onUpliftReady
-
*/

//constants
const ATTR_ID = 'data-g4-id';
const ATTR_PRICE = 'data-g4-price-value';
const ATTR_PRICE_TYPE = 'data-g4-price-type';
const ATTR_PRICE_MODEL = 'data-g4-price-model';
const ATTR_COMPARISON = 'data-g4-comparison-type';
const ATTR_TAX_INCLUDED = 'data-g4-taxes-included';
const ATTR_MONTHLY_AMOUNT = 'data-g4-from-currency-unit-major';

//constants for tripInfo
const TICKET_TYPE = 'digital';
const RESERVATION_TYPE = 'standard';
const UPLIFT_DATE_FORMAT = 'YYYYMMDD';
const TRIP_FLEX = 'TRIP_FLEX';
const PETC = 'PETC';
const ROUND_TRIP = 'ROUND_TRIP';
const ITN_ANCILLARY_ORDER_ITEM = 'ItineraryAncillaryOrderItem';
const TRAVELER_ANCILLARY_ORDER_ITEM = 'TravelerAncillaryOrderItem';
const VEHICLE_ORDER_ITEM = 'VehicleOrderItem';
const SEAT_ORDER_ITEM = 'SeatOrderItem';
const BUNDLE_ORDER_ITEM = 'BundleOrderItem';
const FLIGHT_ORDER_ITEM = 'FlightOrderItem';
const HOTEL_ORDER_ITEM = 'HotelOrderItem';

//helper functions
const centsToDollars = value => {
  if (!value) {
    return null;
  }
  return parseFloat(value / 100).toFixed(2);
};

export const dollarsToCents = value => {
  if (!value) {
    return null;
  }
  const cents = parseFloat(value) * 100;
  // "toFixed" helps in rounding off
  // Ex: $127.9999 without toFixed would be converted to 12799 cents
  // with toFixed it would be converted to 12780 cents
  return parseInt(cents.toFixed());
};

//hook definition
const useUplift = ({
  withLightbox = true,
  confirmationNumber = null,
  isUpliftInitialised,
  setIsUpliftInitialised,
}) => {
  const {
    setIsUpliftAvailable,
    setIsUpliftSelected,
    setIsUpliftToken,
    paymentToken,
    setPaymentToken,
  } = useUpliftState();
  //we are setting initialization in context to avoid double .init calls

  // we need to pass a ref to TOKEN_RETRIEVED, otherwise, the paymentToken value does not update, isEqual return never happens and Payment form constantly rerenders because context is always set again (i.e. infinite API call loop)
  const refToken = useRef(false);
  useEffect(() => {
    refToken.current = paymentToken;
  }, [paymentToken]);

  const updatePaymentTokenValue = useCallback(
    token => {
      setPaymentToken(token);
    },
    [setPaymentToken]
  );

  //confirmation page is not among the booking steps, so the activeStep object returned will be undefined,
  //that's why this form of destructuring is needed
  const { activeStep } = useBookingSteps();
  const currentStep = activeStep ? activeStep.key : 'confirmation';
  const isPaymentStep = currentStep === 'payment';
  const isConfirmationStep = currentStep === 'confirmation';

  //initPayMonthly function
  const initPayMonthly = apiKey => {
    window.UpLift.Payments.init({
      apiKey,
      rawPricing: true, // we have to update DOM nodes manually
      locale: 'en-US',
      currency: 'USD',
      onChange: processResponse,
      ...(isPaymentStep
        ? {
            checkout: true,
            container: '#up-pay-monthly-container',
          }
        : {}),
    });
    setIsUpliftInitialised(true);
  };

  //onChange handler
  const processResponse = response => {
    const offers = response.pricing_offers;
    const nodes = [];
    // if we have offers for the sent IDs, display the nodes related to that ID
    if (offers) {
      keys(offers).forEach(id => {
        // for every available offer, get related DOM node
        // and update data-g4-from-currency-unit-major attribute with the received price
        const node = document?.querySelector(`[${ATTR_ID}="${id}"]`);
        nodes.push(node);
        const monthlyAmountNode = node?.querySelector(
          `[${ATTR_MONTHLY_AMOUNT}]`
        );
        const dollars = centsToDollars(offers[id].monthly_payment_amount);

        if (monthlyAmountNode) {
          monthlyAmountNode.textContent = dollars;

          // attach the UpLift Lightbox
          withLightbox &&
            window.UpLift.Payments.addInfo(node, [node.getAttribute(ATTR_ID)]);
        }
      });
    }

    const statusHandlers = {
      OFFER_AVAILABLE: function() {
        setIsUpliftAvailable(true);
      },
      TOKEN_AVAILABLE: function() {
        window.UpLift.Payments.getToken();
      },
      TOKEN_RETRIEVED: function() {
        // retrieve and utilize the token:
        const token = response.token;
        if (isEqual(token, refToken.current)) {
          return;
        }
        updatePaymentTokenValue(token);
        setIsUpliftToken(true);
      },
      OFFER_UNAVAILABLE: function() {
        setIsUpliftAvailable(false);
      },
      SERVICE_UNAVAILABLE: function() {
        setIsUpliftAvailable(false);
      },
    };
    statusHandlers[response.status]();
  };

  const getInsurance = price => {
    return [
      {
        types: ['cancellation'],
        price: dollarsToCents(price),
      },
    ];
  };

  const getTravelerIdList = travelers =>
    _.reduce(
      travelers,
      (result, traveler, i) => {
        if (traveler.__typename === 'Traveler') {
          result.push(i);
        }
        return result;
      },
      []
    );

  // gives a translated hotels object for buildTripInfo
  const getHotelReservation = (cartItem, travelersIds, insurance = []) => {
    return {
      ticket_type: TICKET_TYPE,
      check_in: moment(cartItem?.check_in?.time).format(UPLIFT_DATE_FORMAT),
      check_out: moment(cartItem?.check_out?.time).format(UPLIFT_DATE_FORMAT),
      reservation_type: RESERVATION_TYPE,
      number_of_rooms: cartItem?.roomsCount,
      traveler_ids: travelersIds,
      price: dollarsToCents(cartItem?.hotelPrice?.total),
      insurance: insurance,
    };
  };

  const getFlightReservation = (cartItem, travelerIdList) => {
    const bookingInfo = {
      reservation_type: RESERVATION_TYPE,
      trip_type:
        cartItem?.price?.discountType === ROUND_TRIP ? 'roundtrip' : 'oneway',
      carrier_code: 'G4',
      fare_class: 'economy',
      price: dollarsToCents(cartItem?.price.total),
      origin: cartItem?.flight?.origin?.displayName,
      destination: cartItem?.flight?.destination?.displayName,
    };

    const itnInfo = {
      departure_apc: cartItem?.flight?.origin?.code,
      arrival_apc: cartItem?.flight?.destination?.code,
      departure_time: moment(cartItem?.flight?.departingTime).format(
        UPLIFT_DATE_FORMAT
      ),
      ticket_type: TICKET_TYPE,
      arrival_city: cartItem?.flight?.destination?.city,
      departure_city: cartItem?.flight?.origin?.city,
      traveler_ids: travelerIdList,
    };

    return {
      itnInfo,
      bookingInfo,
    };
  };

  const getAddOn = (amount, name) => {
    return {
      name: name,
      price: dollarsToCents(amount),
    };
  };

  const translateOrderItemsToTripInfo = (items, addOnsList, travelerIdList) => {
    let itineraryList = [];
    let hotelList = [];
    let insuranceList = [];
    let otherBookingInfo = {};
    let newAddOnList = addOnsList;

    items.forEach(i => {
      if (
        i?.__typename === TRAVELER_ANCILLARY_ORDER_ITEM &&
        !i?.isBundledAncillaryIncluded
      ) {
        const addOnItem = getAddOn(i?.price?.amount, i?.ancillaryType);
        newAddOnList.push(addOnItem);
      }

      if (i?.__typename === VEHICLE_ORDER_ITEM) {
        const addOnItem = getAddOn(
          i?.vehiclePrice?.total?.amount,
          i?.vehicle?.description
        );
        newAddOnList.push(addOnItem);
      }

      if (
        i?.__typename === ITN_ANCILLARY_ORDER_ITEM &&
        !i?.isBundledAncillaryIncluded
      ) {
        if (i?.ancillaryType === TRIP_FLEX) {
          insuranceList = getInsurance(i?.price?.amount);
        } else {
          const addOnItem = getAddOn(i?.price?.amount, i?.name);
          newAddOnList.push(addOnItem);
        }
      }

      if (i?.__typename === SEAT_ORDER_ITEM && !i?.isBundledAncillaryIncluded) {
        const addOnItem = getAddOn(
          i?.price?.amount,
          `Seat ${i?.column}${i?.row}`
        );
        newAddOnList.push(addOnItem);
      }

      if (i?.__typename === BUNDLE_ORDER_ITEM) {
        const ancillaries = i?.bundle?.ancillaries;
        ancillaries.forEach(ancillary => {
          const amount = ancillary?.price?.amount * travelerIdList.length;
          if (ancillary.type === TRIP_FLEX) {
            // calculating the trip-flex pricing for all travelers
            insuranceList = getInsurance(amount);
          } else {
            const addOnItem = getAddOn(amount, ancillary?.name);
            newAddOnList.push(addOnItem);
          }
        });
      }

      if (i?.__typename === FLIGHT_ORDER_ITEM) {
        const { itnInfo, bookingInfo } = getFlightReservation(
          i,
          travelerIdList
        );
        itineraryList.push(itnInfo);
        otherBookingInfo = bookingInfo;
      }

      if (i?.__typename === HOTEL_ORDER_ITEM) {
        const hotelReservation = getHotelReservation(
          i,
          travelerIdList,
          insuranceList
        );
        hotelList.push(hotelReservation);
      }
    });

    return {
      otherBookingInfo,
      newAddOnList,
      itineraryList,
      hotelList,
    };
  };

  //buildTrioInfo function
  const buildTripInfo = order => {
    const { items, travelers, price } = order;
    let travelersList = [];
    let addOnsList = [];
    const travelerIdList = getTravelerIdList(travelers);

    travelers.forEach(t => {
      travelersList.push({
        id: t.id,
        first_name: t?.firstName ?? null,
        last_name: t?.lastName ?? null,
        date_of_birth: t?.dateOfBirth
          ? moment(t.dateOfBirth).format('MM/DD/YYYY')
          : null,
      });

      if (t?.ssrs) {
        t.ssrs.forEach(ssr => {
          if (ssr?.code === PETC) {
            const addOnItem = getAddOn(ssr?.price?.amount, ssr?.title);
            addOnsList.push(addOnItem);
          }
        });
      }
    });

    const {
      itineraryList,
      insuranceList,
      hotelList,
      otherBookingInfo,
      newAddOnList,
    } = translateOrderItemsToTripInfo(items, addOnsList, travelerIdList);

    return {
      order_amount: dollarsToCents(price?.balanceDue), // send order amount in cents
      travelers: travelersList,
      air_reservations: [
        {
          itinerary: itineraryList,
          insurance: insuranceList,
          ...otherBookingInfo,
        },
      ],
      hotel_reservations: hotelList,
      add_ons: newAddOnList,
    };
  };

  //buildPricesInfo function
  const buildPricesInfo = async () => {
    let pricesInfo = {};
    // wait until an UpLift element exists
    await Wait.forElementToExist(`[${ATTR_ID}]`);
    // get all nodes by unique IDs
    const nodes = document?.querySelectorAll(`[${ATTR_ID}]`);
    // create priceInfo object as described in docs
    nodes.forEach(node => {
      const values = {
        [node.getAttribute(ATTR_ID)]: {
          value: dollarsToCents(node.getAttribute(ATTR_PRICE)), // send value in cents
          type: node.getAttribute(ATTR_PRICE_TYPE),
          model: node.getAttribute(ATTR_PRICE_MODEL),
          comparison: node.getAttribute(ATTR_COMPARISON),
          taxIncluded: node.getAttribute(ATTR_TAX_INCLUDED) === 'true',
        },
      };
      pricesInfo = assign(pricesInfo, values);
    });
    return pricesInfo;
  };

  // upReady function
  const onUpLiftReady = async data => {
    // init Pay Monthly
    !isUpliftInitialised && initPayMonthly(data.settings.uplift.apiKey);

    // build tripInfo
    const tripInfo = buildTripInfo(data.order);

    // build pricesInfo (necessary only when using rawPricing=true option for initialization)
    const pricesInfo = await buildPricesInfo();

    if (!isConfirmationStep) {
      window.UpLift.Payments.load(tripInfo, pricesInfo);
    } else {
      window.UpLift.Payments.confirm(confirmationNumber);
    }
  };

  const [selectUplift] = useMutation(SELECT_UPLIFT, {
    refetchQueries: ({ data }) => {
      // if we select a new card type and that card type conflicts with a promo code,
      // we will get a promo error, so we must scroll to promo section, and refetch promos to display error
      if (data && data.useUplift.order.promoError?.errors.length) {
        return [{ query: GET_ORDER_PAYMENTS_QUERY }];
      }
    },
    awaitRefetchQueries: true,
  });

  //selectUpLiftPayment function
  const selectUpLiftPayment = useCallback(() => {
    window.Uplift.Payments.select();
    selectUplift({ variables: { use: true } });
    setIsUpliftSelected(true);
  }, [selectUplift, setIsUpliftSelected]);

  //deselectUpLiftPayment function
  const unselectUpliftPayment = useCallback(() => {
    window.Uplift.Payments.deselect();
    selectUplift({ variables: { use: false } });
    setIsUpliftSelected(false);
  }, [selectUplift, setIsUpliftSelected]);

  //error logging function
  const sendErrorsToUplift = errorMessage => {
    window.Uplift.Payments.error(errorMessage);
  };

  // data needed to initialize uplift
  const { loading, error, data } = useSSRQuery(UPLIFT_CONFIG_QUERY);

  if (!data && loading) {
    setIsUpliftAvailable(false);
    return {
      selectUpLiftPayment: null,
      unselectUpliftPayment: null,
      paymentToken: null,
    };
  }
  // since middleware is no longer returning an error if there are no uplift settings,
  // but a NULL setttings.uplift object, we must check for this case as well
  if (error || !data || data.settings.uplift === null) {
    setIsUpliftAvailable(false);
    return {
      selectUpLiftPayment: null,
      unselectUpliftPayment: null,
      paymentToken: null,
    };
  }

  const apiUrl = '//cdn.uplift-platform.com/a/up.js';
  const { apiKey, apiCode } = data.settings.uplift;

  if (typeof window !== 'undefined') {
    const isGlobalEnabled = data.settings?.uplift?.pages.find(
      p => p.pageName === 'global'
    )?.pageValue;

    if (isGlobalEnabled && apiCode && apiKey) {
      // load UpLift API if needed
      if (!window.UpLift?.Payments) {
        UpLiftAPI.load(apiUrl, apiCode).then(() => {
          onUpLiftReady(data);
        });
      } else {
        // after UpLift API is in place, call UpLift ready
        onUpLiftReady(data);
      }
    }
  }

  return {
    selectUpLiftPayment,
    unselectUpliftPayment,
    paymentToken,
    sendErrorsToUplift,
  };
};

export default useUplift;
